import React, { useEffect, useState } from "react";
import {
  Heading,
  Flex,
  Button,
  Text,
  ButtonGroup,
  Container, useToast
} from "@chakra-ui/react";
import { Link } from "react-router-dom";

import Layout from "@pages/Layout";
import { Separator, View, LoadMore } from "@components";
import { IconAddAnnonce } from "@assets/Icons";
import { loadAnnonces, deleteAnnonce } from "@lib/datahandlers/company";
import { t } from "@lang/i18n";
import { AnnonceItem, ModalBox, CustomSkeleton } from "@components";
import { PER_PAGE } from "@lib/constants";

const AnnonceScreen = () => {
  const initStateLoadMore = { 
    page: 1, 
    has_more: "", 
    loading_more: "" 
  };
  const initStateAnnonces = { 
    archived: [], 
    offers: [] 
  };

  const [annonces, setAnnonces] = useState(initStateAnnonces);
  const [showModalBox, setShowModalBox] = useState(false);
  const [annonceId, setAnnonceId] = useState("");
  const [loadMore, setLoadMore] = useState(initStateLoadMore);
  const [isLoaded, setIsLoaded] = useState(false);

  const toast = useToast();

  const isArrayEmpty = (arr) => {
    if (arr.length > 0) return false;
    return true
  };
  // load annonces
  const onLoad = async () => {
    try {
      setIsLoaded(true);
      // const searchQuery = `/offers?page=1`;
      const result = await loadAnnonces(`/offers`);
      if (result.success) {
        setAnnonces({
            offers: result.data.offers,
            archived: result.data.archived
          });
          setIsLoaded(false);
          return;
        }
        toast({ description: t("messages.error.annonces_get") , status: "error", position: "bottom-right", variant: "left-accent", isClosable: true });

      } catch (err) {
        setIsLoaded(false);
        console.log(err.message);
      }
  };
  // archive annonce
  const onArchive = async (id) => {
    try {
      const result = await deleteAnnonce(id);
      if (result.success) {
        setShowModalBox(false);
        window.location.reload();
        return;
      }
    } catch (err) {
      toast({description: "Une erreur s'est produite durant l'archivage de l'annonce", status: "error", variant: 'subtle', duration: 3000});
    }
  };
  // load more annonces
  const showMore = async () => {
      let nextPage = loadMore.page + 1;
      const searchQuery = `/offers?page=${nextPage}`;
      
      setLoadMore({...loadMore, loading_more: true})
      const result = await await loadAnnonces(searchQuery);
      setLoadMore({...loadMore, loading_more: false})

      if(result.success) {
        setLoadMore({
          ...loadMore,
          has_more: result.data.length === PER_PAGE,
          page: nextPage,
        });
        setAnnonces([...annonces, ...result.data.offers]);
      }
  };

  useEffect(() => {
    if (isArrayEmpty(annonces)) {
      onLoad();
    }
  }, []);

  return (
    <Layout menuType="company" isNavBarVisible isFooterVisible>
      <Container maxW="1024px" bg="white" minHeight='calc(100vh - 100px)' py="36px" px={["16px","","","100px"]} borderRadius="4px">
        <Flex flexDir="column">
          <View if={showModalBox}>
            <ModalBox isOpen={showModalBox} onClose={() => setShowModalBox(false)} >
                <Heading as="h3" fontSize="20px" fontWeight="600"> Archivage </Heading>
                <Text color="black" textAlign="center" fontSize="16px" fontWeight="400" mt="21px" mb="46px">
                  {t("company.annonce.archive.description")}
                </Text>

                <ButtonGroup spacing="6">
                    <Button bg="error" borderRadius="24px" color="white" w="164px" h="40px"
                            _hover={{ opacity: ".85" }}
                            onClick={() => setShowModalBox(false)}>
                        { t('global.close') }
                    </Button>
                    <Button bg="green.main" borderRadius="24px" color="white" w="164px" h="40px"
                        _hover={{ bg: "green.light"}}
                        onClick={() => onArchive(annonceId)}>
                        { t('global.confirm_2') }
                    </Button>
                </ButtonGroup>
            </ModalBox>
          </View>
          
          <Heading as="h1" fontSize="30px" color="black" textAlign="center">
            {t("company.annonce.main_annonce.title")}
          </Heading>

          <Separator />

        {/* Add Annonces Button */}
        <Link to="/company/annonce/add">
          <Flex flexDir="column" w="189px" mx="auto" _hover={{cursor: "pointer"}}>
            <Flex justifyContent="center" alignItems="center" w="119px" h="119px" bg="gray.annonce" borderRadius="50%" mx="auto">
              <IconAddAnnonce size="big" />
            </Flex>
            <Text color="green.main" fontWeight="700" fontSize="18px" lineHeight="24,51px" mt="16px">  
              {t("company.annonce.main_annonce.add")}
            </Text>
          </Flex>
        </Link>


        {/* skeleton loading */}
        <View if={isLoaded} display="flex" flexDir="row" flexWrap="wrap" justifyContent={{base: "center", lg: "space-between"}} mt="49px">
          <CustomSkeleton type="ANNONCE_ITEMS" />
          <CustomSkeleton type="ANNONCE_ITEMS" />
          <CustomSkeleton type="ANNONCE_ITEMS" />
        </View>

        {/* Annonces listing (offers) */}
        <View if={annonces.offers} 
              display="flex" flexWrap="wrap" justifyContent={{base: "center", md: "space-evenly",lg: "space-between"}} mt="49px">
          { annonces.offers.map((el, idx) => 
            <AnnonceItem key={idx} data={el} setShowModalBox={setShowModalBox} setAnnonceId={setAnnonceId} />) 
          }
        </View>

        {/* Annonces listing (archived) */}
        <View if={annonces.archived && annonces.archived.length > 0}
              display="flex" flexWrap="wrap" justifyContent={{base: "center", md: "space-evenly",lg: "space-between"}}>
          { annonces.archived.map((el, idx) => 
            <AnnonceItem key={idx} data={el} setShowModalBox={setShowModalBox} setAnnonceId={setAnnonceId} isArchived />) 
          }
        </View>

        {/* Annonces none */}
        <View if={!annonces || annonces.length === 0}>
          <Text color="gray.borderBoxes" mx="auto" fontWeight="400" fontSize="18px" lineHeight="24,51px" w="409px" mt="155px">  
            {t("company.annonce.main_annonce.annonce_none")}
          </Text> 
        </View> 

        {/* Pagination */}
        <View if={loadMore.has_more} flexBasis="100%" textAlign="center">
          <LoadMore showMore={showMore} loading_more={loadMore.loading_more} />
        </View>
      </Flex>
      </Container>
    </Layout>
  );
};

export default AnnonceScreen;
